import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DataTable,
  DateDisplay,
  EnumValueDisplay,
  IDataTableColumn,
  ShowMoreButton,
  LastSeenDisplay,
} from '@lib/common';
import { Box, Button, Tooltip, Typography } from '@material-ui/core';
import { PowerStatus, PropertyDevice } from 'app/state';
import { ThingType } from '@lib/state';
import React, { useCallback, useMemo } from 'react';
import { DeviceDeleteModal } from './device-delete-modal';

interface Props {
  devices: Array<PropertyDevice>;
  occupiedRooms: Array<string>;
  loading: boolean;
  isDone: boolean;
  onIdentifyDevice: (device: PropertyDevice) => void;
  onDeleteDevice: (thingName: string) => void;
  loadMore: () => void;
  onRowClick: (device: PropertyDevice) => void;
}

export function DeviceList({
  devices,
  occupiedRooms,
  loading,
  isDone,
  onIdentifyDevice,
  onDeleteDevice,
  loadMore,
  onRowClick,
}: Props) {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [thingNameToDelete, setThingNameToDelete] = React.useState<string | null>(null);

  const handleDeleteClick = useCallback(
    (thingName: string) => {
      setThingNameToDelete(thingName);
      setConfirmDeleteOpen(true);
    },
    [setThingNameToDelete, setConfirmDeleteOpen]
  );

  const onDeleteConfirm = useCallback(() => {
    if (thingNameToDelete) {
      onDeleteDevice(thingNameToDelete);
    }
  }, [thingNameToDelete, onDeleteDevice]);

  const onDeleteClose = useCallback(() => {
    setConfirmDeleteOpen(false);
    setThingNameToDelete(null);
  }, [setConfirmDeleteOpen, setThingNameToDelete]);

  const columns = useMemo(() => getColumns(occupiedRooms, onIdentifyDevice, handleDeleteClick), [
    occupiedRooms,
    onIdentifyDevice,
    handleDeleteClick,
  ]);

  return (
    <>
      <DataTable
        items={devices}
        columns={columns}
        loading={loading}
        getItemKey={device => device.thingName || device.macAddress || device.thingIdentifier || ''}
        onRowClick={onRowClick}
      >
        <ShowMoreButton isDone={isDone} loadMore={loadMore} />
      </DataTable>
      <DeviceDeleteModal
        open={confirmDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
}

const getColumns = (
  occupiedRooms: Array<string>,
  onIdentifyDevice: (device: PropertyDevice) => void,
  onDeleteDevice: (thingName: string) => void
): Array<IDataTableColumn<PropertyDevice>> => [
  {
    title: 'Area/Room Name',
    valueFactory: device => (
      <Box display="flex" flexDirection="row" gridGap={8} alignItems="center">
        <Typography>{device.thingIdentifier}</Typography>
        {device.thingIdentifier && occupiedRooms.includes(device.thingIdentifier) && (
          <FontAwesomeIcon icon="user" title="Occupied" />
        )}
      </Box>
    ),
  },
  {
    title: 'Device Type',
    valueFactory: device => <EnumValueDisplay value={device.thingType} />,
  },
  {
    title: 'Status',
    valueFactory: device => {
      const showStatusTooltip = device.powerStatus === PowerStatus.Offline && !!device.lastSeen;

      const statusDisplay = (
        <span>
          <EnumValueDisplay value={device.powerStatus} />
        </span>
      );

      return showStatusTooltip ? (
        <Tooltip title={<LastSeenDisplay lastSeen={device.lastSeen} refreshIntervalMs={10000} />}>
          {statusDisplay}
        </Tooltip>
      ) : (
        statusDisplay
      );
    },
  },
  {
    title: 'Last Seen',
    valueFactory: device => (
      <DateDisplay date={device.lastSeen ?? undefined} dateFormat="MM/dd/yyyy hh:mm aa" />
    ),
  },
  {
    title: 'Version',
    valueFactory: device => <Typography>{device.version ?? 'N/A'}</Typography>,
  },
  {
    title: 'Actions',
    valueFactory: device => (
      <Box display="flex" flexDirection="row" gridGap={8}>
        {device.thingType === ThingType.WaterFlowSensor && (
          <Button
            type="button"
            onClick={event => {
              event.stopPropagation();
              onIdentifyDevice(device);
            }}
          >
            Identify
          </Button>
        )}
        <Button
          type="button"
          onClick={event => {
            event.stopPropagation();
            if (!device.thingName) return;
            onDeleteDevice(device.thingName ?? '');
          }}
        >
          Delete
        </Button>
      </Box>
    ),
  },
];
